import { useNavigate, useParams } from 'react-router-dom'
import { api } from '@/lib/api'
import { hasRate, zeroReasonLabel } from '@/lib/commission'
import { date, money, percent, shortDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { Card, ErrorState, Loading } from '@/components/ui/primitives'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { StatementBadge } from './Statements'
import type { PortalStatementDetail, PortalStatementLine } from '@/types/api'

/**
 * One statement and its working.
 *
 * The screen that decides whether this portal was worth building: "why was I paid 9,235
 * in August" has to be answerable from the record rather than from a telephone call to
 * finance. So every row shows the payment it came from - the date, the deal, the customer
 * and the net amount - beside the rate applied to it. A commission figure with nothing
 * behind it is a number a partner has to take on trust.
 *
 * Nothing here can be edited, by anybody. A mistake is corrected by reversing the
 * payment, which reverses the accrual by the same arithmetic.
 */
export function Statement() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const detail = useAsync<PortalStatementDetail>(
    (signal) => api.get<PortalStatementDetail>(`/portal/statements/${id}`, undefined, signal),
    [id],
  )

  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) {
    return (
      <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />
    )
  }
  if (!detail.data) return null

  const { statement, lines } = detail.data

  return (
    <>
      <PageHeader
        eyebrow="Statement"
        title={statement.reference}
        hint={`${date(statement.periodStart)} to ${date(statement.periodEnd)}`}
        back={{ label: 'Statements', onClick: () => navigate('/statements') }}
        actions={<StatementBadge status={statement.status} />}
      />

      <Card className="mb-5 px-5 py-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-faint">Total</p>
        <p className="tabular mt-1 text-2xl font-semibold">
          {money(statement.totalAmount, statement.currencyCode)}{' '}
          <span className="text-sm font-normal text-muted">{statement.currencyCode}</span>
        </p>
        <p className="mt-1 text-[12px] text-muted">
          {lines.length} {lines.length === 1 ? 'line' : 'lines'}, issued{' '}
          {date(statement.issuedAt)}.
        </p>
      </Card>

      <Card>
        <Table>
          <thead>
            <tr>
              <Th>Payment received</Th>
              <Th>Deal</Th>
              <Th>Product</Th>
              <Th numeric>Payment (net)</Th>
              <Th numeric>Year</Th>
              <Th>Basis</Th>
              <Th numeric>Rate</Th>
              <Th numeric>Commission</Th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <Line
                // Accruals carry no id in this contract, and two lines can legitimately
                // agree on every visible field - the same deal paid twice in a month for
                // the same amount. The index is the only honest key here.
                key={index}
                line={line}
                currencyCode={statement.currencyCode}
              />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line">
              <Td className="font-medium">Total</Td>
              <Td />
              <Td />
              <Td />
              <Td />
              <Td />
              <Td />
              <Td numeric className="font-semibold whitespace-nowrap">
                {money(statement.totalAmount, statement.currencyCode)}{' '}
                <span className="text-[11px] text-faint">{statement.currencyCode}</span>
              </Td>
            </tr>
          </tfoot>
        </Table>
      </Card>

      <p className="mt-4 max-w-3xl text-[12px] text-muted">
        Every line resolves to a payment Codemax has received, on a named deal and a named
        product, at the rate fixed when that quotation was submitted &mdash; changing a
        commission plan
        afterwards can never change what an issued deal is worth. Nothing on this statement
        can be edited: a mistake is corrected by reversing the payment, which reverses the
        commission by the same arithmetic.
      </p>
    </>
  )
}

function Line({ line, currencyCode }: { line: PortalStatementLine; currencyCode: string }) {
  const nil = line.zeroReason !== null

  return (
    <Tr>
      <Td className="whitespace-nowrap">{shortDate(line.paidOn)}</Td>
      <Td>
        <span className="tabular block whitespace-nowrap">
          {line.quotationNo ?? <span className="text-faint">&mdash;</span>}
        </span>
        {line.customerName && (
          <span className="block text-[12px] text-muted">{line.customerName}</span>
        )}
        {line.soldByResellerName && (
          // An override line. Naming the partner who sold it is what makes the figure
          // explainable - without it this is a deal the reader has never seen.
          <span className="block text-[12px] text-spark">
            Won by {line.soldByResellerName}
          </span>
        )}
      </Td>
      <Td className="text-muted">
        {line.productName ?? <span className="text-faint">&mdash;</span>}
      </Td>
      <Td numeric className="whitespace-nowrap">
        {money(line.paymentNetAmount, currencyCode)}
      </Td>
      <Td numeric>{line.contractYear}</Td>
      <Td>{line.billingKind === 'OneTime' ? 'One-time' : 'Recurring'}</Td>
      <Td numeric>
        {hasRate(line.zeroReason) ? (
          percent(line.percentApplied)
        ) : (
          <span className="text-faint">&mdash;</span>
        )}
      </Td>
      <Td numeric className="whitespace-nowrap">
        {money(line.amount, currencyCode)}
        {/* A nil line says which zero it is. An absent row would be silence, and silence
            is what generates the email to finance. */}
        {nil && line.zeroReason && (
          <span className="ml-1.5 text-[11px] text-muted">
            &middot; {zeroReasonLabel(line.zeroReason)}
          </span>
        )}
      </Td>
    </Tr>
  )
}
